<template>
  <!-- Import Order Details Dialog -->
  <BaseDialog
    v-model="showImportDialog"
    title="Process Order Files"
    :max-width="1000"
    toolbar-color="secondary"
    :show-save="!!(importData && importSelectedRows.length > 0)"
    :save-text="`Save ${importSelectedRows.length} Record${importSelectedRows.length > 1 ? 's' : ''}`"
    :save-loading="importSaving"
    :save-disabled="importSaving"
    save-color="teal"
    cancel-text="Close"
    @save="saveSelectedRecords"
  >
    <!-- Import Method Tabs -->
    <v-tabs v-model="importTab" color="secondary" class="mx-n4 mt-n4 mb-4">
      <v-tab value="upload">
        <v-icon start>mdi-file-upload</v-icon>
        Upload File
      </v-tab>
      <v-tab value="download">
        <v-icon start>mdi-download</v-icon>
        Download from platform
      </v-tab>
    </v-tabs>

    <v-window v-model="importTab">
      <!-- Tab 1: Upload File -->
      <v-window-item value="upload" class="pt-4">
        <v-form ref="uploadForm" @submit.prevent="processImportFile">
          <!-- Row 1: Format + file select -->
          <v-row density="compact" class="mb-1">
            <v-col cols="5" sm="5">
              <v-select
                v-model="importPlatform"
                :items="importablePlatforms"
                label="Format"
                variant="outlined"
                density="compact"
                :disabled="importLoading"
                hide-details
                :rules="[v => !!v || 'Required']"
              >
                <template v-slot:item="{ item, props }">
                  <v-list-item v-bind="props">
                    <template v-slot:prepend>
                      <div :style="{ background: getPlatformGradient(item), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                    </template>
                  </v-list-item>
                </template>
                <template v-slot:selection="{ item }">
                  <div class="d-flex align-center">
                    <div :style="{ background: getPlatformGradient(item), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
                    {{ item }}
                  </div>
                </template>
              </v-select>
            </v-col>
            <v-col cols="5" sm="5">
              <v-file-input
                v-model="importFile"
                :label="!importPlatform ? 'Select a format first' : importPlatform === 'Direct Sales' ? 'Select HTML files' : 'Select file'"
                :accept="importPlatform === 'Direct Sales' ? '.html,.htm' : '.xlsx,.xls,.csv'"
                prepend-icon=""
                :multiple="importPlatform === 'Direct Sales'"
                variant="outlined"
                density="compact"
                :disabled="importLoading || !importPlatform"
                hide-details
              />
            </v-col>
            <v-col cols="2" sm="2" class="d-flex align-center">
              <v-btn
                color="secondary"
                variant="flat"
                type="submit"
                :loading="importLoading"
                :disabled="!importFile || !importPlatform"
                height="40"
                block
              >
                <v-icon start>mdi-upload</v-icon>
                Process
              </v-btn>
            </v-col>
          </v-row>

          <!-- Row 2: Coverage date range + info button -->
          <v-row density="compact" class="align-center">
            <v-col cols="5" sm="5">
              <v-text-field
                v-model="importDateFrom"
                label="Coverage from"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                :max="today"
                @update:model-value="onCoverageFromChange"
              />
            </v-col>
            <v-col cols="5" sm="5">
              <v-text-field
                v-model="importDateTo"
                label="Coverage to"
                type="date"
                variant="outlined"
                density="compact"
                hide-details
                clearable
                :max="today"
                @update:model-value="onCoverageToChange"
              />
            </v-col>
            <v-col cols="2" sm="2" class="d-flex align-center">
              <v-btn size="small" variant="text" @click="showCoverageInfo = true">
                <v-icon start size="small">mdi-help-circle-outline</v-icon>
                Virtual Records
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-window-item>

      <!-- Tab 2: Download from platform -->
      <v-window-item value="download" class="pt-4">
        <!-- Platform selector -->
        <v-select
          v-model="downloadPlatform"
          :items="downloadablePlatforms"
          label="Platform"
          variant="outlined"
          density="compact"
          :disabled="talabatFetchLoading"
          hide-details
          class="mb-3"
        >
          <template v-slot:item="{ item, props }">
            <v-list-item v-bind="props">
              <template v-slot:prepend>
                <div :style="{ background: getPlatformGradient(item.title), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
              </template>
            </v-list-item>
          </template>
          <template v-slot:selection="{ item }">
            <div class="d-flex align-center">
              <div :style="{ background: getPlatformGradient(item.title), width: '14px', height: '14px', borderRadius: '3px', marginRight: '6px' }"></div>
              {{ item.title }}
            </div>
          </template>
        </v-select>

        <!-- Date range + download button (or date preset for Deliverect/Keeta) -->
        <div v-if="!usesDatePreset" class="d-flex ga-2 align-center mb-3">
          <v-text-field
            v-model="talabatFetchFrom"
            label="From"
            type="date"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="talabatFetchLoading"
            class="flex-grow-1"
          />
          <v-text-field
            v-model="talabatFetchTo"
            label="To"
            type="date"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="talabatFetchLoading"
            class="flex-grow-1"
          />
          <v-btn
            color="secondary"
            variant="flat"
            @click="handleDownload"
            :loading="talabatFetchLoading"
            :disabled="!downloadPlatform || !talabatFetchFrom || !talabatFetchTo"
            height="40"
          >
            <v-icon start>mdi-download</v-icon>
            Download
          </v-btn>
        </div>

        <!-- Date preset for Deliverect/Keeta -->
        <div v-else class="d-flex ga-2 align-center mb-3">
          <v-select
            v-model="currentDatePreset"
            :items="currentDatePresets"
            label="Period"
            variant="outlined"
            density="compact"
            hide-details
            :disabled="talabatFetchLoading"
            class="flex-grow-1"
          />
          <v-btn
            color="secondary"
            variant="flat"
            @click="handleDownload"
            :loading="talabatFetchLoading"
            :disabled="!downloadPlatform"
            height="40"
          >
            <v-icon start>mdi-download</v-icon>
            Download
          </v-btn>
        </div>

        <v-alert
          v-if="talabatFetchError"
          type="error"
          variant="outlined"
          density="compact"
          closable
          @click:close="talabatFetchError = null"
        >
          {{ talabatFetchError }}
        </v-alert>
      </v-window-item>
    </v-window>

    <v-divider class="my-4"></v-divider>

    <!-- Initial State -->
    <div
      v-if="!talabatFetchLoading && !importLoading && !importData && !importError"
      class="d-flex align-center justify-center"
      style="min-height: 200px;"
    >
      <v-empty-state
        headline=""
        title=""
        :text="importTab === 'download'? 'Select a (platform) file format and date range, then click Download' : 'Select an Excel file and click Process File'"
        :icon="importTab === 'download' ? 'mdi-download-outline' : 'mdi-file-excel-outline'"
      ></v-empty-state>
    </div>

    <!-- Loading State -->
    <div v-if="importLoading || talabatFetchLoading" class="text-center py-8">
      <v-progress-circular indeterminate color="teal"></v-progress-circular>
      <div class="mt-2 text-grey">
        {{ talabatFetchLoading ? (fetchProgressMessage || `Downloading from ${downloadPlatform}...`) : "Processing orders' file..." }}
      </div>
    </div>

    <!-- Error State -->
    <v-alert v-else-if="importError" type="error" variant="outlined" class="mb-4">
      {{ importError }}
    </v-alert>

    <!-- Results -->
    <div v-else-if="importData">
      <!-- Warnings -->
      <v-alert
        v-if="importData.warnings?.length > 0"
        type="warning"
        variant="outlined"
        class="mb-4"
        density="compact"
      >
        <div v-for="(warning, idx) in importData.warnings" :key="idx">
          {{ warning }}
        </div>
      </v-alert>

      <!-- Save Result Message (positioned at top of results for visibility) -->
      <v-alert
        v-if="importSaveResult"
        :type="importSaveResult.success ? 'success' : 'error'"
        variant="outlined"
        class="mb-4"
        density="compact"
        closable
        @click:close="importSaveResult = null"
      >
        <template v-if="importSaveResult.success">
          {{ importSaveResult.message }}
          <span v-if="importSaveResult.errors?.length > 0" class="text-warning">
            ({{ importSaveResult.errors.length }} errors)
          </span>
        </template>
        <template v-else>
          {{ importSaveResult.error }}
        </template>
      </v-alert>

      <!-- Manual Conflict Warning: shown when import skipped hand-entered records -->
      <v-alert
        v-if="importSaveResult?.manualConflicts?.length > 0 && !showManualConflictDialog"
        type="warning"
        variant="outlined"
        class="mb-4"
        density="compact"
      >
        {{ importSaveResult.manualConflicts.length }} record(s) were skipped because they would overwrite manually-entered data.
        <v-btn variant="text" size="small" color="warning" class="ml-2" @click="showManualConflictDialog = true">
          Review
        </v-btn>
      </v-alert>

      <!-- Brand / Location Breakdown Table -->
      <v-table v-if="importBreakdown.length > 0" density="compact" class="mb-4 border rounded">
        <thead>
          <tr>
            <th>Brand</th>
            <th>Location</th>
            <th class="text-center">Start</th>
            <th class="text-center">End</th>
            <th class="text-center">Days</th>
            <th class="text-center">Orders</th>
            <th class="text-end">Gross</th>
            <th class="text-end">Net</th>
            <th class="text-center">Disc %</th>
            <th class="text-center">Rows</th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in importBreakdown" :key="`${row.brand}|${row.location}`">
            <td>{{ row.brand }}</td>
            <td>{{ row.location }}</td>
            <td class="text-center">{{ formatDate(row.startDate) }}</td>
            <td class="text-center">{{ formatDate(row.endDate) }}</td>
            <td class="text-center">{{ row.days }}</td>
            <td class="text-center text-teal">{{ row.orders }}</td>
            <td class="text-end">{{ formatAmount(row.grossAmount) }}</td>
            <td class="text-end">{{ formatAmount(row.amount) }}</td>
            <td class="text-center text-orange">{{ row.discountPct != null ? `${row.discountPct}%` : '-' }}</td>
            <td class="text-center">{{ row.rows }}</td>
          </tr>
        </tbody>
      </v-table>

      <!-- Filters -->
      <v-row class="mb-4">
        <v-col cols="6" sm="2">
          <v-text-field
            v-model="importFilterStartDate"
            label="From Date"
            type="date"
            :min="importDateRange.min"
            :max="importFilterEndDate || importDateRange.max"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="6" sm="2">
          <v-text-field
            v-model="importFilterEndDate"
            label="To Date"
            type="date"
            :min="importFilterStartDate || importDateRange.min"
            :max="importDateRange.max"
            density="compact"
            variant="outlined"
            hide-details
            clearable
          ></v-text-field>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="importFilterCity"
            :items="uniqueCities"
            label="City"
            clearable
            density="compact"
            variant="outlined"
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="3">
          <v-select
            v-model="importFilterLocation"
            :items="uniqueLocations"
            label="Location"
            clearable
            density="compact"
            variant="outlined"
            hide-details
          ></v-select>
        </v-col>
        <v-col cols="12" sm="2">
          <v-select
            v-model="importFilterBrand"
            :items="uniqueBrands"
            label="Brand"
            clearable
            density="compact"
            variant="outlined"
            hide-details
          ></v-select>
        </v-col>
      </v-row>

      <!-- Unmapped Restaurants Warning -->
      <v-alert
        v-if="importData.unmapped?.length > 0"
        type="warning"
        variant="outlined"
        class="mb-4"
        density="compact"
      >
        <template v-slot:prepend>
          <v-icon>mdi-alert-outline</v-icon>
        </template>
        <div class="d-flex align-center justify-space-between">
          <span>
            {{ importData.unmapped.length }} restaurant(s) without mapping (using fallback
            parsing)
          </span>
          <v-btn size="small" variant="text" color="warning" @click="showUnmappedDialog = true">
            View Details
          </v-btn>
        </div>
      </v-alert>

      <!-- Select All / Deselect All buttons -->
      <div class="d-flex justify-end ga-2 mb-3">
        <v-btn
          size="small"
          variant="outlined"
          @click="selectAllFilteredRecords"
          :disabled="filteredImportData.length === 0 || importSelectedRows.length === filteredImportData.length"
        >
          <v-icon start size="small">mdi-checkbox-multiple-marked</v-icon>
          Select All ({{ filteredImportData.length }})
        </v-btn>
        <v-btn
          size="small"
          variant="outlined"
          @click="deselectAllRecords"
          :disabled="importSelectedRows.length === 0"
        >
          <v-icon start size="small">mdi-checkbox-multiple-blank-outline</v-icon>
          Deselect All
        </v-btn>
      </div>

      <v-divider class="mt-0 mb-4"></v-divider>

      <!-- Summary Card -->
      <v-card variant="outlined" class="mb-4">
        <v-card-text class="pa-3">
          <v-row>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold">{{ filteredImportSummary.days }}</div>
              <div class="text-caption text-grey">Days</div>
            </v-col>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold text-teal">
                {{ filteredImportSummary.orders }}
              </div>
              <div class="text-caption text-grey">Orders</div>
            </v-col>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ formatAmount(filteredImportSummary.grossAmount) }}
              </div>
              <div class="text-caption text-grey">Gross AED</div>
            </v-col>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold">
                {{ formatAmount(filteredImportSummary.amount) }}
              </div>
              <div class="text-caption text-grey">Net AED</div>
            </v-col>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold text-orange">
                {{ filteredImportSummary.discountPct != null ? `${filteredImportSummary.discountPct}%` : '-' }}
              </div>
              <div class="text-caption text-grey">Discount</div>
            </v-col>
            <v-col cols="6" sm="2" class="text-center">
              <div class="text-h5 font-weight-bold">{{ filteredImportSummary.rows }}</div>
              <div class="text-caption text-grey">Rows</div>
            </v-col>
          </v-row>
          <div
            v-if="importSelectedRows.length > 0"
            class="text-caption text-center mt-2 text-teal"
          >
            {{ importSelectedRows.length }} rows selected ({{
              selectedImportSummary.orders
            }}
            orders, {{ formatAmount(selectedImportSummary.amount) }} AED)
          </div>
          <div v-else class="text-caption text-center mt-2 text-grey">
            Date range: {{ importData.summary.dateRange.start }} to
            {{ importData.summary.dateRange.end }}
            <span v-if="importData.summary.mappingsUsed > 0" class="ml-2">
              | {{ importData.summary.mappingsUsed }} mapping(s) available
            </span>
          </div>
        </v-card-text>
      </v-card>

      <!-- Per-Brand Breakdown -->
      <div v-if="brandBreakdown.length > 1" class="mb-4 d-flex flex-wrap ga-3">
        <v-chip v-for="b in brandBreakdown" :key="b.brand" variant="outlined" size="small" label>
          <strong>{{ b.brand }}</strong>
          <span class="ml-2 text-grey-darken-1">{{ b.orders }} orders</span>
          <span class="ml-2">{{ formatAmount(b.amount) }}</span>
          <span v-if="b.discountPct != null" class="ml-1 text-orange">({{ b.discountPct }}%)</span>
        </v-chip>
      </div>

      <!-- Data Table with Selection -->
      <!-- Virtual table for performance with large datasets -->
      <v-data-table-virtual
        v-model="importSelectedRows"
        :headers="tableHeaders"
        :items="filteredImportData"
        height="400"
        density="compact"
        class="elevation-1"
        show-select
        item-value="id"
        return-object
        :row-props="getRowProps"
      >
        <template v-slot:item.date="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">
            {{ formatDate(item.date) }} <span class="text-medium-emphasis">{{ formatDayOfWeek(item.date) }}</span>
            <v-tooltip v-if="item.virtual" location="top">
              <template v-slot:activator="{ props }">
                <v-icon v-bind="props" size="x-small" class="ml-1">mdi-calendar-blank</v-icon>
              </template>
              No orders on this date
            </v-tooltip>
          </span>
        </template>
        <template v-slot:item.amount="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">
            {{ formatAmount(item.amount) }}
          </span>
        </template>
        <template v-slot:item.discountPct="{ item }">
          <span
            :class="{ 'text-orange': item.discountPct > 20, 'virtual-row-text': item.virtual }"
          >
            {{ item.discountPct != null ? `${item.discountPct}%` : '-' }}
          </span>
        </template>
        <template v-slot:item.city="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">{{ item.city }}</span>
        </template>
        <template v-slot:item.location="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">{{ item.location }}</span>
        </template>
        <template v-slot:item.brand="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">{{ item.brand }}</span>
        </template>
        <template v-slot:item.orders="{ item }">
          <span :class="{ 'virtual-row-text': item.virtual }">{{ item.orders }}</span>
        </template>
      </v-data-table-virtual>
    </div>

    <!-- Manual Conflicts Confirmation Dialog -->
    <v-dialog v-model="showManualConflictDialog" max-width="600" persistent>
      <v-card>
        <v-toolbar color="warning" density="compact">
          <v-toolbar-title>Overwrite Manual Records?</v-toolbar-title>
          <v-spacer />
          <v-btn icon size="small" @click="skipManualConflicts">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <!-- Explanation of the conflict -->
          <p class="mb-3">
            The following <strong>{{ manualConflicts.length }}</strong> record(s) were entered manually
            and would be overwritten by this import. What would you like to do?
          </p>

          <!-- Conflict records table -->
          <v-table density="compact" class="border rounded">
            <thead>
              <tr>
                <th>Date</th>
                <th>Location</th>
                <th>Brand</th>
                <th>Platform</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="(c, i) in manualConflicts" :key="i">
                <td>{{ c.date }}</td>
                <td>{{ c.location }}</td>
                <td>{{ c.brand }}</td>
                <td>{{ c.platform }}</td>
              </tr>
            </tbody>
          </v-table>
        </v-card-text>

        <v-card-actions>
          <!-- Skip: keep the manual records untouched -->
          <v-btn variant="outlined" @click="skipManualConflicts">Skip</v-btn>
          <v-spacer />
          <!-- Overwrite: re-send the conflict records with forceOverwriteManual flag -->
          <v-btn color="warning" variant="flat" @click="forceOverwriteManualRecords">
            Overwrite All
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Saving Overlay -->
    <v-overlay
      :model-value="importSaving"
      contained
      class="align-center justify-center"
      persistent
    >
      <div class="text-center">
        <v-progress-circular indeterminate color="teal" size="64"></v-progress-circular>
        <div class="mt-4 text-white text-h6">Saving records...</div>
      </div>
    </v-overlay>
  </BaseDialog>

  <!-- Unmapped Restaurants Dialog -->
  <BaseDialog
    v-model="showUnmappedDialog"
    title="Unmapped Restaurants"
    :max-width="700"
    toolbar-color="warning"
    :show-save="false"
  >
    <p class="text-body-2 mb-4">
      These restaurants were not found in the mapping table. Fallback parsing was used to
      extract city, location, and brand. Consider adding mappings for more accurate data.
    </p>

    <v-data-table
      :headers="unmappedHeaders"
      :items="importData?.unmapped || []"
      :items-per-page="10"
      density="compact"
      class="elevation-1"
    >
      <template v-slot:item.parsedCity="{ item }">
        <span :class="{ 'text-red': !item.parsedCity }">
          {{ item.parsedCity || "N/A" }}
        </span>
      </template>
      <template v-slot:item.parsedLocation="{ item }">
        <span :class="{ 'text-red': !item.parsedLocation }">
          {{ item.parsedLocation || "N/A" }}
        </span>
      </template>
      <template v-slot:item.parsedBrand="{ item }">
        <span :class="{ 'text-red': !item.parsedBrand }">
          {{ item.parsedBrand || "N/A" }}
        </span>
      </template>
    </v-data-table>
  </BaseDialog>

  <!-- Snackbar for save feedback -->
  <!-- Snackbar: shows "Review" action when manual conflicts were skipped -->
  <v-snackbar v-model="showSnackbar" :color="snackbarColor" :timeout="snackbarHasConflicts ? 8000 : 4000" location="top">
    {{ snackbarMessage }}
    <template v-slot:actions>
      <v-btn v-if="snackbarHasConflicts" variant="text" @click="showManualConflictDialog = true; showSnackbar = false">
        Review
      </v-btn>
      <v-btn variant="text" @click="showSnackbar = false">Close</v-btn>
    </template>
  </v-snackbar>

  <!-- Coverage Date Info Dialog -->
  <BaseDialog
    v-model="showCoverageInfo"
    title="Virtual Record Coverage"
    :max-width="540"
    :show-save="false"
  >
    <!-- Explanation of virtual records -->
    <div class="text-body-2 px-4 pt-3 pb-1">
      When importing sales data, the system generates <strong>virtual zero-value records</strong>
      for every location/brand combination that has active business rules for this platform,
      filling each day in the coverage range. This ensures complete coverage reporting — days
      and locations with no sales still appear as explicit zeros rather than missing data.
    </div>

    <v-divider class="my-2" />

    <!-- Coverage date rules -->
    <v-list density="compact" class="bg-transparent">
      <v-list-subheader>How coverage dates are set</v-list-subheader>
      <v-list-item prepend-icon="mdi-calendar-today">
        <strong>Coverage from</strong> — defaults to the earliest date in the imported data.
        Follows the start date filter when active.
      </v-list-item>
      <v-list-item prepend-icon="mdi-calendar-check">
        <strong>Coverage to</strong> — defaults to the latest date in the imported data.
        Follows the end date filter when active. Cannot exceed today's date.
      </v-list-item>
      <v-list-item prepend-icon="mdi-pencil">
        You can manually override either date — your values are honoured and won't
        be overwritten by filters.
      </v-list-item>
      <v-list-item prepend-icon="mdi-information-outline">
        Clear both dates to skip virtual record generation and import only the actual file data.
      </v-list-item>
    </v-list>
  </BaseDialog>
</template>

<script setup>
import { ref, computed, watch, onMounted } from "vue";
import BaseDialog from "@/components/BaseDialog.vue";
import { useSalesMenu } from "@/composables/useSalesMenu.js";
import { useApp } from "@/composables/useApp.js";
import api from "@/utils/api.js";
import { usePlatformColors } from "@/composables/usePlatformColors.js";

const { getPlatformGradient } = usePlatformColors();

const { options } = useApp();

// Platform column mappings (determines which platforms can be imported)
const columnMappings = ref([]);

const {
  showImportDialog,
  importFile,
  importPlatform,
  importLoading,
  importError,
  importData,
  importSelectedRows,
  importFilterStartDate,
  importFilterEndDate,
  importFilterCity,
  importFilterLocation,
  importFilterBrand,
  importSaving,
  importSaveResult,
  importDateFrom,
  importDateTo,
  importBreakdown,
  importDateRange,
  filteredImportData,
  filteredImportSummary,
  brandBreakdown,
  selectedImportSummary,
  closeImportDialog,
  processImportFile,
  saveSelectedRecords,
  selectAllFilteredRecords,
  deselectAllRecords,
  // Manual conflict state
  manualConflicts,
  showManualConflictDialog,
  forceOverwriteManualRecords,
  skipManualConflicts,
  // Platform fetch
  talabatFetchFrom,
  talabatFetchTo,
  talabatFetchLoading,
  talabatFetchError,
  fetchProgressMessage,
  fetchFromPlatform,
} = useSalesMenu();

const importTab = ref("upload");
const downloadPlatform = ref(null);
const showUnmappedDialog = ref(false);
const showCoverageInfo = ref(false);

// Today's date (ISO string) — used as max for coverage dates
const today = new Date().toISOString().split("T")[0];

// Track whether user manually set coverage dates
const coverageFromManuallySet = ref(false);
const coverageToManuallySet = ref(false);

// Called when user changes coverage-from via the input
function onCoverageFromChange(v) {
  coverageFromManuallySet.value = !!v;
  if (v && !importDateTo.value) importDateTo.value = v;
}

// Called when user changes coverage-to via the input
function onCoverageToChange(v) {
  coverageToManuallySet.value = !!v;
}

// Auto-set coverage dates when import data arrives
watch(importData, (data) => {
  if (!data?.summary?.dateRange) return;
  // Default coverage-from to earliest date in data (unless user manually set it)
  if (!coverageFromManuallySet.value) {
    importDateFrom.value = data.summary.dateRange.start;
  }
  // Default coverage-to to latest date in data (unless user manually set it)
  if (!coverageToManuallySet.value) {
    importDateTo.value = data.summary.dateRange.end;
  }
});

// When filter dates change, update coverage dates to match (unless manually set)
watch(importFilterStartDate, (newVal) => {
  if (!coverageFromManuallySet.value && importData.value) {
    importDateFrom.value = newVal || importData.value.summary?.dateRange?.start || null;
  }
});
watch(importFilterEndDate, (newVal) => {
  if (!coverageToManuallySet.value && importData.value) {
    importDateTo.value = newVal || importData.value.summary?.dateRange?.end || null;
  }
});

// Check if a platform using date presets is selected (hides date inputs)
const isDeliverectSelected = computed(() => downloadPlatform.value === "Deliverect");
const isKeetaSelected = computed(() => downloadPlatform.value === "Keeta");
const usesDatePreset = computed(() => isDeliverectSelected.value || isKeetaSelected.value);

// Date presets for platforms that use them
const deliverectDatePresets = [
  "Yesterday",
  "Today",
  "Tomorrow",
  "This Week",
  "Last Week",
  "This Month",
  "Last Month",
  "Last Three Months",
];
const keetaDatePresets = ["Today", "Last 7 days", "Last 30 days", "This week", "This month"];

const deliverectDatePreset = ref("Last Week");
const keetaDatePreset = ref("Last 7 days");

// Get the appropriate preset value and list based on selected platform
const currentDatePresets = computed(() =>
  isDeliverectSelected.value ? deliverectDatePresets : keetaDatePresets
);
const currentDatePreset = computed({
  get: () => (isDeliverectSelected.value ? deliverectDatePreset.value : keetaDatePreset.value),
  set: (val) => {
    if (isDeliverectSelected.value) {
      deliverectDatePreset.value = val;
    } else {
      keetaDatePreset.value = val;
    }
  },
});

// Handle download with platform-specific options
function handleDownload() {
  if (usesDatePreset.value) {
    fetchFromPlatform(downloadPlatform.value, { datePreset: currentDatePreset.value });
  } else {
    fetchFromPlatform(downloadPlatform.value);
  }
}

// Snackbar state
const showSnackbar = ref(false);
const snackbarMessage = ref("");
const snackbarColor = ref("success");
// Tracks whether the snackbar should show a "Review" button for manual conflicts
const snackbarHasConflicts = ref(false);

// Watch for save result to show snackbar (includes conflict count when applicable)
watch(importSaveResult, (result) => {
  if (result) {
    if (result.success) {
      const created = result.created || 0;
      const updated = result.updated || 0;
      const conflictCount = result.manualConflicts?.length || 0;
      const base = result.message || `Created ${created} new, updated ${updated} existing records`;
      snackbarMessage.value = conflictCount > 0
        ? `${base} — ${conflictCount} manual record(s) skipped`
        : base;
      snackbarColor.value = conflictCount > 0 ? "warning" : "success";
      snackbarHasConflicts.value = conflictCount > 0;
    } else {
      snackbarMessage.value = result.error || "Failed to save records";
      snackbarColor.value = "error";
      snackbarHasConflicts.value = false;
    }
    showSnackbar.value = true;
  }
});

// Auto-set end date when start date is entered
watch(importFilterStartDate, (newVal) => {
  if (newVal && !importFilterEndDate.value) {
    importFilterEndDate.value = newVal;
  }
});

// Clear file when platform changes (upload tab)
watch(importPlatform, () => {
  importFile.value = null;
});

// Clear results when switching between upload/download tabs
watch(importTab, () => {
  importData.value = null;
  importError.value = null;
  importSelectedRows.value = [];
  importSaveResult.value = null;
  coverageFromManuallySet.value = false;
  coverageToManuallySet.value = false;
});

// Clear import data when download platform changes
watch(downloadPlatform, () => {
  importData.value = null;
  importError.value = null;
  importSelectedRows.value = [];
  importSaveResult.value = null;
});

// Fetch column mappings to determine importable platforms
async function fetchColumnMappings() {
  try {
    const response = await api.accountingApi.get("/api/accounting/sales/platform-column-mappings?include_inactive=true");
    columnMappings.value = response.data || [];
  } catch (err) {
    console.error("Failed to fetch column mappings:", err);
    columnMappings.value = [];
  }
}

// Platforms available for file upload (only active ones)
const importablePlatforms = computed(() => {
  if (!columnMappings.value?.length) return [];
  return columnMappings.value
    .filter((m) => m.is_active)
    .map((m) => m.platform)
    .sort();
});

// Platforms for the download tab
const downloadablePlatforms = computed(() => {
  return columnMappings.value
    .filter((m) => m.is_active || m.auto_download_enabled)
    .map((m) => ({
      title: m.platform,
      value: m.platform,
      props: m.auto_download_enabled ? {} : { disabled: true },
    }))
    .sort((a, b) => a.title.localeCompare(b.title));
});

// Reset tab and fetch column mappings when dialog is opened
watch(showImportDialog, (isOpen) => {
  if (isOpen) {
    importTab.value = "upload";
    downloadPlatform.value = null;
    coverageFromManuallySet.value = false;
    coverageToManuallySet.value = false;
    if (columnMappings.value.length === 0) {
      fetchColumnMappings();
    }
  }
});

const tableHeaders = [
  { title: "Date", key: "date", sortable: true },
  { title: "Source", key: "source", sortable: true },
  { title: "Platform", key: "platform", sortable: true },
  { title: "City", key: "city", sortable: true },
  { title: "Location", key: "location", sortable: true },
  { title: "Brand", key: "brand", sortable: true },
  { title: "Net (AED)", key: "amount", sortable: true, align: "end" },
  { title: "Disc %", key: "discountPct", sortable: true, align: "end" },
  { title: "Orders", key: "orders", sortable: true, align: "center" },
];

const unmappedHeaders = [
  { title: "Restaurant ID", key: "id", sortable: true },
  { title: "Restaurant Name", key: "name", sortable: true },
  { title: "Orders", key: "orders", sortable: true, align: "center" },
  { title: "Parsed City", key: "parsedCity", sortable: true },
  { title: "Parsed Location", key: "parsedLocation", sortable: true },
  { title: "Parsed Brand", key: "parsedBrand", sortable: true },
];

// Filter options from database
const uniqueCities = computed(() => {
  if (options.cities?.length) {
    return options.cities.map((c) => c.name).sort();
  }
  if (!importData.value?.data) return [];
  return [...new Set(importData.value.data.map((r) => r.city))].sort();
});

const uniqueLocations = computed(() => {
  if (options.locations?.length) {
    let locs = options.locations;
    if (importFilterCity.value) {
      const cityObj = options.cities?.find((c) => c.name === importFilterCity.value);
      if (cityObj) {
        locs = locs.filter((l) => l.city_id === cityObj.id);
      }
    }
    return locs.map((l) => l.name).sort();
  }
  if (!importData.value?.data) return [];
  let data = importData.value.data;
  if (importFilterCity.value) {
    data = data.filter((r) => r.city === importFilterCity.value);
  }
  return [...new Set(data.map((r) => r.location))].sort();
});

const uniqueBrands = computed(() => {
  if (options.brands?.length) {
    return options.brands.map((b) => b.name).sort();
  }
  if (!importData.value?.data) return [];
  return [...new Set(importData.value.data.map((r) => r.brand))].sort();
});

function formatAmount(amount) {
  return new Intl.NumberFormat("en-AE", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(amount);
}

function formatDate(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function formatDayOfWeek(dateStr) {
  if (!dateStr) return "";
  const date = new Date(dateStr + "T00:00:00");
  return date.toLocaleDateString(undefined, { weekday: "short" });
}

// Row styling for virtual records (missing dates)
function getRowProps({ item }) {
  if (item.virtual) {
    return { class: "virtual-row" };
  }
  return {};
}
</script>

<style scoped>
.border-t {
  border-top: 1px solid rgba(0, 0, 0, 0.12);
}

.virtual-row-text {
  color: #000;
}

</style>

<style>
/* Unscoped style for row background (Vuetify renders rows outside scoped context) */
.v-data-table .virtual-row {
  background-color: #D6D3D1 !important;
}
</style>
